/**
 * InvoicePdfTemplate — React-PDF document component for rendering invoice PDFs.
 *
 * Uses @react-pdf/renderer to produce a professional A4 invoice layout with:
 *   - Company header with branding
 *   - Invoice metadata (number, date, due date, status)
 *   - Bill-to and project sections
 *   - Line items table
 *   - Subtotal, VAT, and grand total
 *   - Payment terms and notes
 *   - Fixed footer with page numbers
 */

import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from '@react-pdf/renderer';
import type { InvoicePdfData } from '@/types/invoice-pdf';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: '#1a1a1a',
  },
  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 30,
  },
  companyInfo: {
    flex: 1,
  },
  companyName: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 4,
  },
  companyAddress: {
    fontSize: 9,
    color: '#555555',
    lineHeight: 1.4,
  },
  invoiceTitle: {
    textAlign: 'right' as const,
  },
  invoiceTitleText: {
    fontSize: 28,
    fontFamily: 'Helvetica-Bold',
    color: '#2563eb',
    marginBottom: 8,
  },
  invoiceMeta: {
    fontSize: 9,
    color: '#555555',
    textAlign: 'right' as const,
    lineHeight: 1.6,
  },
  invoiceMetaLabel: {
    fontFamily: 'Helvetica-Bold',
    color: '#1a1a1a',
  },
  // Divider
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    marginVertical: 16,
  },
  // Bill To section
  billToSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  billToBlock: {
    flex: 1,
  },
  billToLabel: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: 1,
    marginBottom: 6,
  },
  billToName: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 2,
  },
  billToDetail: {
    fontSize: 9,
    color: '#555555',
    lineHeight: 1.4,
  },
  projectBlock: {
    flex: 1,
    alignItems: 'flex-end' as const,
  },
  // Table
  table: {
    marginBottom: 24,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#2563eb',
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  tableHeaderCell: {
    color: '#ffffff',
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  tableRowAlt: {
    backgroundColor: '#f9fafb',
  },
  tableCell: {
    fontSize: 9,
  },
  // Column widths
  colDescription: { width: '40%' },
  colQuantity: { width: '12%', textAlign: 'right' as const },
  colPrice: { width: '16%', textAlign: 'right' as const },
  colValue: { width: '16%', textAlign: 'right' as const },
  colCode: { width: '16%' },
  // Totals
  totalsSection: {
    alignItems: 'flex-end' as const,
    marginBottom: 24,
  },
  totalsTable: {
    width: 240,
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  totalsLabel: {
    fontSize: 9,
    color: '#555555',
  },
  totalsValue: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
  },
  totalsDivider: {
    borderBottomWidth: 2,
    borderBottomColor: '#2563eb',
    marginVertical: 4,
  },
  grandTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
    paddingHorizontal: 8,
    backgroundColor: '#eff6ff',
  },
  grandTotalLabel: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: '#2563eb',
  },
  grandTotalValue: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: '#2563eb',
  },
  // Notes & Payment Terms
  notesSection: {
    marginTop: 16,
  },
  notesLabel: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: 1,
    marginBottom: 4,
  },
  notesText: {
    fontSize: 9,
    color: '#555555',
    lineHeight: 1.4,
  },
  // Footer
  footer: {
    position: 'absolute' as const,
    bottom: 30,
    left: 40,
    right: 40,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 7,
    color: '#9ca3af',
  },
  footerPage: {
    fontSize: 7,
    color: '#9ca3af',
  },
});

function pdfFormatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(amount);
}

function pdfFormatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

interface InvoicePdfTemplateProps {
  data: InvoicePdfData;
}

export function InvoicePdfTemplate({ data }: InvoicePdfTemplateProps) {
  const { transaction, companyDetails, lineItems, paymentTerms, notes } = data;

  // Calculate subtotal from line items
  const subtotal = lineItems.reduce((sum, line) => sum + line.value, 0);

  // VAT calculation (20% standard UK rate)
  const vatRate = 0.2;
  const vatAmount = subtotal * vatRate;
  const grandTotal = subtotal + vatAmount;

  // Due date: use provided dueDate, or fall back to 30 days after invoice date
  const dueDate = transaction.dueDate
    ? transaction.dueDate
    : (() => {
        const txDate = new Date(transaction.date);
        const d = new Date(txDate);
        d.setDate(d.getDate() + 30);
        return d.toISOString();
      })();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>{companyDetails.name}</Text>
            <Text style={styles.companyAddress}>
              {companyDetails.addressLine1}
              {companyDetails.addressLine2 ? `\n${companyDetails.addressLine2}` : ''}
              {`\n${companyDetails.city}, ${companyDetails.postcode}`}
              {`\n${companyDetails.country}`}
              {companyDetails.phone ? `\nTel: ${companyDetails.phone}` : ''}
              {companyDetails.email ? `\n${companyDetails.email}` : ''}
            </Text>
            {companyDetails.vatNumber && (
              <Text style={[styles.companyAddress, { marginTop: 4 }]}>
                VAT No: {companyDetails.vatNumber}
              </Text>
            )}
            {companyDetails.companyNumber && (
              <Text style={styles.companyAddress}>
                Company No: {companyDetails.companyNumber}
              </Text>
            )}
          </View>
          <View style={styles.invoiceTitle}>
            <Text style={styles.invoiceTitleText}>INVOICE</Text>
            <Text style={styles.invoiceMeta}>
              <Text style={styles.invoiceMetaLabel}>Invoice No: </Text>
              {transaction.invoiceNumber}
              {'\n'}
              <Text style={styles.invoiceMetaLabel}>Date: </Text>
              {pdfFormatDate(transaction.date)}
              {'\n'}
              <Text style={styles.invoiceMetaLabel}>Due Date: </Text>
              {pdfFormatDate(dueDate)}
              {'\n'}
              <Text style={styles.invoiceMetaLabel}>Status: </Text>
              {transaction.status}
            </Text>
          </View>
        </View>

        <View style={styles.divider} />

        {/* Bill To / Project */}
        <View style={styles.billToSection}>
          <View style={styles.billToBlock}>
            <Text style={styles.billToLabel}>Bill To</Text>
            <Text style={styles.billToName}>
              {transaction.accountName || 'Client'}
            </Text>
          </View>
          {transaction.projectName && (
            <View style={styles.projectBlock}>
              <Text style={styles.billToLabel}>Project</Text>
              <Text style={styles.billToName}>{transaction.projectName}</Text>
            </View>
          )}
        </View>

        {/* Line Items Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.colDescription]}>Description</Text>
            <Text style={[styles.tableHeaderCell, styles.colCode]}>Code</Text>
            <Text style={[styles.tableHeaderCell, styles.colQuantity]}>Qty</Text>
            <Text style={[styles.tableHeaderCell, styles.colPrice]}>Unit Price</Text>
            <Text style={[styles.tableHeaderCell, styles.colValue]}>Value</Text>
          </View>

          {/* Table Rows */}
          {lineItems.map((line, index) => (
            <View
              key={index}
              style={[
                styles.tableRow,
                index % 2 === 1 ? styles.tableRowAlt : {},
              ]}
            >
              <Text style={[styles.tableCell, styles.colDescription]}>{line.description}</Text>
              <Text style={[styles.tableCell, styles.colCode]}>{line.code}</Text>
              <Text style={[styles.tableCell, styles.colQuantity]}>{line.quantity.toFixed(2)}</Text>
              <Text style={[styles.tableCell, styles.colPrice]}>{pdfFormatCurrency(line.unitPrice)}</Text>
              <Text style={[styles.tableCell, styles.colValue]}>{pdfFormatCurrency(line.value)}</Text>
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={styles.totalsSection}>
          <View style={styles.totalsTable}>
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>Subtotal</Text>
              <Text style={styles.totalsValue}>{pdfFormatCurrency(subtotal)}</Text>
            </View>
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>VAT ({(vatRate * 100).toFixed(0)}%)</Text>
              <Text style={styles.totalsValue}>{pdfFormatCurrency(vatAmount)}</Text>
            </View>
            <View style={styles.totalsDivider} />
            <View style={styles.grandTotalRow}>
              <Text style={styles.grandTotalLabel}>Total Due</Text>
              <Text style={styles.grandTotalValue}>{pdfFormatCurrency(grandTotal)}</Text>
            </View>
          </View>
        </View>

        {/* Payment Terms */}
        {paymentTerms && (
          <View style={styles.notesSection}>
            <Text style={styles.notesLabel}>Payment Terms</Text>
            <Text style={styles.notesText}>{paymentTerms}</Text>
          </View>
        )}

        {/* Notes */}
        {notes && (
          <View style={styles.notesSection}>
            <Text style={styles.notesLabel}>Notes</Text>
            <Text style={styles.notesText}>{notes}</Text>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>
            {companyDetails.name} | {transaction.invoiceNumber}
          </Text>
          <Text
            style={styles.footerPage}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
